import React, { useRef, useState } from 'react';
import { asset } from '../data/assets';

/**
 * The 2D trick, recreated.
 *
 * This is what the original inspiration was doing: a flat image that reads as
 * dimensional because it tilts towards the pointer, drifts on a slow loop, and
 * drags a shadow around with it. No geometry, no model — the depth is entirely
 * in how it responds.
 *
 * Worth having on the page because the whole project starts with wanting to know
 * whether the real thing would be better than this. Side by side, the honest
 * answer is that this is very good for what it costs.
 */
export function FloatingCard() {
  const box = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({
    x: 0,
    y: 0
  });
  const [failed, setFailed] = useState(false);
  const follow = (event: React.PointerEvent<HTMLDivElement>) => {
    const node = box.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    // -1..1 from the centre, so the tilt is symmetric whatever the box size.
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({
      x: x * 2,
      y: y * 2
    });
  };
  return <div data-id="element-12" data-mp="25zan0v3ewfvn">
      <div ref={box} onPointerMove={follow} onPointerLeave={() => setTilt({
      x: 0,
      y: 0
    })} className="grid aspect-video place-items-center overflow-hidden bg-surface" style={{
      perspective: '900px'
    }} data-id="element-13" data-mp="25zkd0iogin1c">
        {failed ? <p className="px-6 text-center text-[13px] text-ink-faint" data-id="element-14" data-mp="2yq2q8ciwa4sh">
            Image did not load — check the URLs in <code data-id="element-15" data-mp="32re79tnpdhp2">data/assets.ts</code>.
          </p> : <div className="floating-card relative" style={{
        transform: `rotateY(${tilt.x * 9}deg) rotateX(${tilt.y * -9}deg)`,
        transformStyle: 'preserve-3d',
        transition: 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1)'
      }} data-id="element-16" data-mp="2oxn7ve3ore72">
            {/* Cast rather than drawn: it slides opposite the tilt, which is the
                cue that sells the whole thing. */}
            <div aria-hidden="true" className="absolute inset-x-6 bottom-0 h-5 rounded-[50%] bg-ink/20 blur-md" style={{
          transform: `translate3d(${tilt.x * -22}px, 14px, -60px) scale(${1 - Math.abs(tilt.x) * 0.12})`,
          transition: 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1)'
        }} data-id="element-17" data-mp="3rapq2fumudvd" />
            <img src={asset('portrait.jpg')} alt="A stylised 3D character, shown flat" onError={() => setFailed(true)} className="relative block h-[260px] w-auto rounded-xl shadow-[0_18px_40px_-18px_rgba(13,18,22,0.45)] sm:h-[300px]" draggable={false} data-id="element-18" data-mp="38jorhsxfckcc" />
          </div>}
      </div>

      <style data-id="element-19" data-mp="2pl2og8ickj5s">{`
        .floating-card { animation: float-bob 5.5s ease-in-out infinite; }
        @keyframes float-bob {
          0%, 100% { translate: 0 0; }
          50%      { translate: 0 -12px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .floating-card { animation: none; }
        }
      `}</style>
    </div>;
}